import Env from "./env";
import {
  getProjectTypeData,
  ProjectContainerKeys,
  ProjectContainerTypes,
  ProjectDistributionMethodName,
  ProjectKeys,
  ProjectTypes
} from "../constants/project";
import { IProjectDetails } from "../interfaces/project";
import { includes } from "lodash";
import { CWRouteParams } from "../constants/router";

export enum PCNavTitles {
  Images = 'Images',
  Certification = `Certification`,
  Security = `Security`,
  RepositoryInformation = `Repository Information`,
  ComponentDetails = 'Component Details',
  ContactInfo = `Contact Info`,
  AssociatedProduct = `Associated Product`,
  UpdateGraph = 'Update Graph',
  OptionalQualifications = 'Optional Qualifications'
}

export interface PCNavListItemType {
  text: PCNavTitles;
  certificationTypes?: string[];
  path: string;
  route: string[];
  exact: boolean;
  postFixName?: string;
}
export const GetProductCentricNavList = (projectDetails: IProjectDetails, routeParams: CWRouteParams): PCNavListItemType[] => {
  const {
    id,
    productId = ''
  } = routeParams;
  const containerTypes = ['container', 'scratch', 'RHEL', 'OpenStack-infra-container', 'OpenStack-app-container'];
  // RHEL-NonContainer type doesnt exist its due to a custom code written,
  // check src/constants/project.ts
  const nonContainerType = ['RHEL-NonContainer', `OpenStack-infra-noncontainer`, 'OpenStack-vnf', ProjectTypes.openshiftCNF, ProjectTypes.openShiftVirtualization]
  let navList: PCNavListItemType[] = [
    {
      text: PCNavTitles.Images,
      certificationTypes: [...containerTypes],
      path: `${Env.routePrefix}/${productId}/${id}/images`,
      route: [`${Env.routePrefix}/:productId/:id/images`],
      exact: false,
      postFixName: 'attached'
    },
    {
      text: PCNavTitles.Certification,
      certificationTypes: [...containerTypes, 'operator bundle image', 'Helm Chart', ...nonContainerType],
      path: `${Env.routePrefix}/${productId}/${id}/certification`,
      route: [`${Env.routePrefix}/:productId/:id/certification`],
      exact: false,
      postFixName: 'certified'
    },
    {
      text: PCNavTitles.OptionalQualifications,
      certificationTypes: ["operator bundle image", "Helm Chart"],
      path: `${Env.routePrefix}/${productId}/${id}/optional-qualifications`,
      route: [`${Env.routePrefix}/:productId/:id/optional-qualifications`],
      exact: true,
      postFixName: 'optional_qualifications'
    },
    {
      text: PCNavTitles.Security,
      certificationTypes: [...containerTypes],
      path: `${Env.routePrefix}/${productId}/${id}/security`,
      route: [`${Env.routePrefix}/:productId/:id/security`],
      exact: false
    },
    {
      text: PCNavTitles.RepositoryInformation,
      certificationTypes: [...containerTypes, 'operator bundle image', 'Helm Chart'],
      path: `${Env.routePrefix}/${productId}/${id}/repository-info`,
      route: [`${Env.routePrefix}/:productId/:id/repository-info`],
      exact: true,
      postFixName: 'repository_details'
    },
    {
      text: PCNavTitles.ComponentDetails,
      certificationTypes: [...containerTypes, 'operator bundle image', 'Helm Chart', ...nonContainerType],
      path: `${Env.routePrefix}/${productId}/${id}/component-details`,
      route: [`${Env.routePrefix}/:productId/:id/component-details`],
      exact: true,
      postFixName: 'component_details'
    },
    {
      text: PCNavTitles.ContactInfo,
      certificationTypes: [...containerTypes, 'operator bundle image', 'Helm Chart', ...nonContainerType],
      path: `${Env.routePrefix}/${productId}/${id}/contact-info`,
      route: [`${Env.routePrefix}/:productId/:id/contact-info`],
      exact: true,
      postFixName: 'contact_details'
    },
    {
      text: PCNavTitles.AssociatedProduct,
      certificationTypes: [...containerTypes, 'operator bundle image', 'Helm Chart', ...nonContainerType],
      path: `${Env.routePrefix}/${productId}/${id}/associated-product`,
      route: [`${Env.routePrefix}/:productId/:id/associated-product`],
      exact: true,
      postFixName: 'associated_product'
    },
    {
      text: PCNavTitles.UpdateGraph,
      certificationTypes: ['operator bundle image'],
      path: `${Env.routePrefix}/${productId}/${id}/update-graph`,
      route: [`${Env.routePrefix}/:productId/:id/update-graph`],
      exact: true
    }
  ];
  const type = getProjectTypeData(projectDetails, true); // include rhel NC
  navList = navList.filter((item) => {
    return includes(item.certificationTypes, type);
  });
  // Filtering out images tab for rhel non-containerised project
  if (IsRHELNonContainerized(projectDetails)) {
    navList = navList.filter((item) => item.text !== PCNavTitles.Images);
  }
  // Filtering out OptionalQualifications tab for cni csi operators
  if (projectDetails?.[ProjectKeys.badges]?.includes("cni") || projectDetails?.[ProjectKeys.badges]?.includes("csi")) {
    navList = navList.filter((item) => item.text !== PCNavTitles.OptionalQualifications);
  }
  return navList;
};

export const IsRHELNonContainerized = (projectDetails) => projectDetails && projectDetails[ProjectKeys.type] === ProjectTypes.RHEL && !projectDetails[ProjectKeys.container];

export const IsRHELContainerized = (projectDetails) => projectDetails && projectDetails[ProjectKeys.type] === ProjectTypes.RHEL && projectDetails[ProjectKeys.container];

export const IsExternalContainerProject = (projectDetails: IProjectDetails): boolean => {
  return (((projectDetails || {})[ProjectKeys.container] || {})[ProjectContainerKeys.distributionMethod] === ProjectDistributionMethodName.external);
}

export const IsOperatorBundleProject = (projectDetails: IProjectDetails): boolean => {
  return (((projectDetails || {})[ProjectKeys.container] || {})[ProjectContainerKeys.type] === ProjectContainerTypes.OPERATOR_BUNDLE_IMAGE);
}

export const IsHelmChartProject = (projectDetails: IProjectDetails): boolean => {
  return (((projectDetails || {})[ProjectKeys.type] || {}) === ProjectTypes.HelmChart);
}
export const IsContainerProject = (projectDetails: IProjectDetails) => {
  const projectType = getProjectTypeData(projectDetails, true);
  return projectType === ProjectContainerTypes.CONTAINER ||
    projectType === ProjectTypes.RHEL ||
    projectType === ProjectTypes.openStackAppContainer ||
    projectType === ProjectTypes.openStackInfraContainer ||
    projectType === ProjectContainerTypes.SCRATCH;
};
